import { Button } from "@blueprintjs/core";
import { Buttons } from "modules/common/components/form/Buttons";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { texts } from "modules/common/texts";
import React from "react";
import styles from "modules/common/components/form/StandardFormButtons.module.scss";
import own from "./PlanSubmitButtons.module.scss";

interface PlanSubmitButtonsProps {
    disabled: boolean;
    canUndo: boolean;
    /** Сохранять и отменять нечего (правок нет), а полоса видна ради других кнопок */
    nothingToSave?: boolean;
    /**
     * Недоступные кнопки не показывать, а не гасить: «Сохранить» и «Отмена» — только
     * при правках, «Отменить действие» — только когда есть шаг назад
     */
    hideUnavailable?: boolean;
    onSubmit: () => void;
    onCancel: () => void;
    onUndo: () => void;
    /** Дополнительные кнопки справа от «Отменить действие» */
    extra?: React.ReactNode;
}

/**
 * Кнопки сохранения плана. Повторяют StandardFormButtons в режиме
 * submitOnDirty + isNewlyCreated (только «Сохранить» и «Отмена»), но без Formik:
 * состояние страницы плана живёт не в форме.
 */
export const PlanSubmitButtons: React.FC<PlanSubmitButtonsProps> = ({
    disabled,
    canUndo,
    onSubmit,
    onCancel,
    onUndo,
    nothingToSave,
    hideUnavailable,
    extra,
}) => {
    const showSave = !hideUnavailable || !nothingToSave;
    const showUndo = !hideUnavailable || canUndo;

    return (
        <Buttons
            className={`${styles.buttons} ${own.bar}`}
            left={
                <>
                    {showSave && (
                        <>
                            <Button
                                text={texts.save}
                                intent="success"
                                className={nothingToSave ? undefined : styles.pulse}
                                disabled={disabled || nothingToSave}
                                onClick={onSubmit}
                                style={{
                                    color: "#fff",
                                    backgroundColor: "#1DD278",
                                    fontFamily: "GothamPro, Arial, sans-serif",
                                    borderRadius: "4px",
                                    border: "none",
                                    fontSize: "14px",
                                    padding: "6px 18px",
                                    transition: "0.2s ease-in",
                                }}
                                onMouseOver={(e: React.MouseEvent<HTMLElement>) => {
                                    e.currentTarget.style.backgroundColor = "#1dc470";
                                }}
                                onMouseOut={(e: React.MouseEvent<HTMLElement>) => {
                                    e.currentTarget.style.backgroundColor = "#1DD278";
                                }}
                            />

                            <Button
                                text={texts.cancel}
                                disabled={disabled || nothingToSave}
                                onClick={onCancel}
                                style={{
                                    color: "rgba(0, 39, 61, 0.4)",
                                    backgroundColor: "#EDF0F2",
                                    fontFamily: "GothamPro, Arial, sans-serif",
                                    borderRadius: "4px",
                                    border: "none",
                                    fontSize: "14px",
                                    padding: "6px 18px",
                                    transition: "0.2s ease-in",
                                }}
                                onMouseOver={(e: React.MouseEvent<HTMLElement>) => {
                                    e.currentTarget.style.backgroundColor = "#e3e6e7";
                                }}
                                onMouseOut={(e: React.MouseEvent<HTMLElement>) => {
                                    e.currentTarget.style.backgroundColor = "#EDF0F2";
                                }}
                            />
                        </>
                    )}

                    {showUndo && (
                        <PlanrButton
                            type="secondary"
                            size="small"
                            icon="general-undo"
                            className={own.undo}
                            disabled={disabled || !canUndo}
                            onClick={onUndo}
                            title="Отменить последнее действие"
                        >
                            Отменить действие
                        </PlanrButton>
                    )}

                    {extra}
                </>
            }
        />
    );
};
